import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { FUNCOES, REGIOES } from "@/components/ResponsaveisConfig";
import { AcessoNegado } from "@/components/AcessoNegado";
import { FuncoesPersonalizadasDialog, labelFuncao } from "@/components/FuncoesPersonalizadas";

const ROLES = {
  administrador: { label: "Administrador", cls: "bg-onyx text-white", perms: "Acesso total ao sistema (pode operar todos os setores)" },
  analista: { label: "Analista", cls: "bg-blue-100 text-blue-700", perms: "Clientes, solicitações, análise e documentos" },
  financeiro: { label: "Financeiro", cls: "bg-emerald-100 text-emerald-700", perms: "Parcelas, pagamentos, baixas e relatórios" },
  agente_campo: { label: "Agente de Campo", cls: "bg-amber-100 text-amber-800", perms: "Carteira atribuída: ver contratos, dar baixa, propor acordos e rota de cobrança" },
};

function CarteiraPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [nomes, setNomes] = useState({});
  useEffect(() => {
    if (q.length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get(`/clients?q=${encodeURIComponent(q)}`).then(({ data }) => setRes(data.slice(0, 8))), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const falta = value.filter((id) => !nomes[id]);
    if (!falta.length) return;
    Promise.all(falta.map((id) => api.get(`/clients/${id}`).then(({ data }) => [id, data.nome]).catch(() => [id, id.slice(0, 8)]))).then((pairs) => setNomes((p) => ({ ...p, ...Object.fromEntries(pairs) })));
  }, [value, nomes]);
  return (
    <div data-testid="carteira-picker">
      <Label className="text-xs">Carteira (clientes atribuídos)</Label>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente por nome/CPF…" data-testid="carteira-busca" className="mt-1" />
      {res.length > 0 && <div className="border rounded-md mt-1 max-h-40 overflow-y-auto">{res.filter((c) => !value.includes(c.id)).map((c) => (
        <button key={c.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => { onChange([...value, c.id]); setNomes((p) => ({ ...p, [c.id]: c.nome })); setQ(""); }} data-testid={`carteira-add-${c.id}`}>{c.nome} <span className="text-slate-400 text-xs">{c.cpf}</span></button>
      ))}</div>}
      <div className="flex flex-wrap gap-1 mt-2">{value.map((id) => (
        <Badge key={id} variant="outline" className="gap-1" data-testid={`carteira-item-${id}`}>{nomes[id] || "…"}<button type="button" onClick={() => onChange(value.filter((x) => x !== id))} className="text-red-500 ml-1">×</button></Badge>
      ))}{!value.length && <span className="text-xs text-slate-400">Nenhum cliente atribuído — o agente não verá nada.</span>}</div>
    </div>
  );
}

function UserForm({ initial, onSaved, trigger, custom = [] }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || { name: "", email: "", password: "", role: "analista", active: true, funcao: "", regiao: "", carteira: [], aprova_acordos: false, acesso_portal_admin: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.email) { toast.error("Preencha nome e email"); return; }
    setLoading(true);
    try {
      if (initial?.id) { await api.put(`/users/${initial.id}`, f); toast.success("Usuário salvo!"); }
      else {
        const { data } = await api.post("/users", f);
        toast.success(data.convite_enviado ? "Usuário criado! Convite enviado por e-mail." : "Usuário criado!");
      }
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Editar Usuário" : "Novo Usuário"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nome</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="uf-name" className="mt-1" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="uf-email" className="mt-1" /></div>
          <div><Label className="text-xs">Senha {initial ? "(deixe vazio para manter)" : "(deixe vazio para enviar convite por e-mail)"}</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="uf-password" className="mt-1" />{!initial && !f.password && <p className="text-[11px] text-emerald-700 mt-1" data-testid="uf-invite-hint">O usuário receberá um e-mail de boas-vindas com link para criar a própria senha.</p>}</div>
          <div><Label className="text-xs">Função de acesso</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="uf-role" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.role !== "agente_campo" && (
            <div><Label className="text-xs">Função (identificação nos textos)</Label>
              <Select value={f.funcao || "none"} onValueChange={(v) => set("funcao", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="uf-funcao" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{FUNCOES.filter(([k]) => k !== "agente_campo").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}{custom.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.role === "agente_campo" && (
            <>
              <div><Label className="text-xs">Região</Label>
                <Select value={f.regiao || "none"} onValueChange={(v) => set("regiao", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="uf-regiao" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{REGIOES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <CarteiraPicker value={f.carteira || []} onChange={(v) => set("carteira", v)} />
            </>
          )}
          {f.role === "financeiro" && (
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border"><span className="text-sm">Pode aprovar acordos do Agente de Campo</span><Switch checked={!!f.aprova_acordos} onCheckedChange={(v) => set("aprova_acordos", v)} data-testid="uf-aprova-acordos" /></div>
          )}
          {f.role !== "agente_campo" && f.role !== "administrador" && (
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border"><span className="text-sm">Pode administrar o acesso ao Portal (aba Portal)</span><Switch checked={!!f.acesso_portal_admin} onCheckedChange={(v) => set("acesso_portal_admin", v)} data-testid="uf-portal-admin" /></div>
          )}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border">
            <span className="text-sm">Ativo</span><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="uf-active" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={loading} className="bg-onyx text-white" data-testid="uf-save">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [negado, setNegado] = useState(false);
  const [custom, setCustom] = useState([]);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); api.get("/funcoes-personalizadas").then((r) => setCustom(r.data.itens || [])).catch(() => {}); }
    catch (e) { if (e.response?.status === 403) setNegado(true); else toast.error(e.response?.data?.detail || "Erro ao carregar usuários"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (negado) return <AcessoNegado area="a gestão de usuários" />;

  return (
    <div className="space-y-5 fade-up" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-extrabold tracking-tight">Usuários</h1><p className="text-slate-500 text-sm">Gerencie acessos e permissões.</p></div>
        <div className="flex flex-wrap gap-2">
          <FuncoesPersonalizadasDialog itens={custom} onSaved={setCustom} />
          <UserForm custom={custom} onSaved={load} trigger={<Button className="bg-onyx hover:bg-black text-white gap-2" data-testid="new-user-btn"><UserPlus className="w-4 h-4" /> Novo Usuário</Button>} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(ROLES).map(([k, v]) => (
          <div key={k} className="trt-card p-4"><Badge className={v.cls}>{v.label}</Badge><p className="text-xs text-slate-500 mt-2">{v.perms}</p></div>
        ))}
      </div>

      <div className="trt-card overflow-x-auto">
        <table className="w-full text-sm min-w-[40rem]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Função</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></td></tr>
              : users.map((u) => (
                <tr key={u.id} className="border-t hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                  <td className="px-4 py-3"><Badge className={ROLES[u.role]?.cls || "bg-slate-100"}>{ROLES[u.role]?.label || u.role}</Badge>{u.regiao && <span className="ml-1 text-xs text-slate-500">· {u.regiao}</span>}{u.funcao && u.role !== "agente_campo" && <span className="ml-1 text-xs text-slate-500">· {labelFuncao(u.funcao, custom)}</span>}</td>
                  <td className="px-4 py-3">{u.active ? <span className="text-emerald-600">Ativo</span> : <span className="text-red-500">Inativo</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <ResetPasswordDialog user={u} />
                    <UserForm custom={custom} initial={{ ...u, password: "" }} onSaved={load} trigger={<Button size="sm" variant="ghost" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const gen = () => setPw(Math.random().toString(36).slice(2, 8) + "@" + Math.floor(100 + Math.random() * 900));
  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { nova_senha: pw });
      toast.success(`Senha de ${user.name} redefinida. Informe a nova senha ao usuário.`); setOpen(false); setPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao redefinir"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="text-amber-600" title="Redefinir senha" data-testid={`reset-pw-${user.id}`}><KeyRound className="w-4 h-4" /></Button></DialogTrigger>
      <DialogContent data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle>Redefinir senha — {user.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Defina uma nova senha para <b>{user.email}</b>. A conta será reativada e bloqueios de login removidos.</p>
        <div className="flex gap-2"><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nova senha (mín. 8)" data-testid="reset-pw-input" /><Button variant="outline" onClick={gen} data-testid="reset-pw-gen">Gerar</Button></div>
        <DialogFooter><Button onClick={save} disabled={loading || pw.length < 8} className="bg-onyx text-white" data-testid="reset-pw-save">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redefinir"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
